// i18n-optimizer.ts - Additional optimization utilities for i18n

interface LanguageCache {
    messages: Record<string, string>
    timestamp: number
    version: string
}

class I18nOptimizer {
    private static instance: I18nOptimizer
    private cache: Map<string, LanguageCache> = new Map()
    private readonly CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 hours
    private readonly CACHE_VERSION = '1.0.0'
    
    private constructor() {}
    
    static getInstance(): I18nOptimizer {
        if (!I18nOptimizer.instance) {
            I18nOptimizer.instance = new I18nOptimizer()
        }
        return I18nOptimizer.instance
    }
    
    // Check if cached language data is still valid
    private isCacheValid(locale: string): boolean {
        const cached = this.cache.get(locale)
        if (!cached) return false
        
        const now = Date.now()
        return (now - cached.timestamp) < this.CACHE_DURATION && 
               cached.version === this.CACHE_VERSION
    }
    
    // Get cached language data
    getCachedLanguage(locale: string): Record<string, string> | null {
        if (this.isCacheValid(locale)) {
            return this.cache.get(locale)?.messages || null
        }
        return null
    }
    
    // Cache language data
    cacheLanguage(locale: string, messages: Record<string, string>): void {
        this.cache.set(locale, {
            messages,
            timestamp: Date.now(),
            version: this.CACHE_VERSION
        })
    }
    
    // Clear expired cache entries
    clearExpiredCache(): void {
        const now = Date.now()
        for (const [locale, cached] of this.cache.entries()) {
            if ((now - cached.timestamp) >= this.CACHE_DURATION) {
                this.cache.delete(locale)
            }
        }
    }
    
    // Preload specific language for better UX
    async preloadLanguage(locale: string, loadFunction: (locale: string) => Promise<Record<string, string>>): Promise<void> {
        if (this.isCacheValid(locale)) return
        
        try {
            const messages = await loadFunction(locale)
            this.cacheLanguage(locale, messages)
        } catch (error) {
            console.warn(`Failed to preload language ${locale}:`, error)
        }
    }
    
    // Get performance metrics
    getPerformanceMetrics(): { cacheSize: number; cacheHitRate: number } {
        const totalRequests = this.cache.size
        const cacheHits = Array.from(this.cache.values()).filter(cached => 
            (Date.now() - cached.timestamp) < this.CACHE_DURATION
        ).length
        
        return {
            cacheSize: totalRequests,
            cacheHitRate: totalRequests > 0 ? (cacheHits / totalRequests) * 100 : 0
        }
    }
}

// Language loading queue for better resource management
class LanguageQueue {
    private queue: Array<{ locale: string; priority: number }> = []
    private processing = false
    private maxConcurrent = 3
    private activeLoads = 0
    
    async addToQueue(locale: string, priority: number = 1): Promise<void> {
        this.queue.push({ locale, priority })
        this.queue.sort((a, b) => b.priority - a.priority) // Higher priority first
        
        if (!this.processing) {
            this.processQueue()
        }
    }
    
    private async processQueue(): Promise<void> {
        if (this.processing || this.queue.length === 0) return
        
        this.processing = true
        
        while (this.queue.length > 0 && this.activeLoads < this.maxConcurrent) {
            const { locale } = this.queue.shift()!
            this.activeLoads++
            
            // Process language loading
            this.loadLanguage(locale).finally(() => {
                this.activeLoads--
            })
        }
        
        this.processing = false
        
        // Continue processing if there are more items
        if (this.queue.length > 0) {
            setTimeout(() => this.processQueue(), 100)
        }
    }
    
    private async loadLanguage(locale: string): Promise<void> {
        // This would integrate with your actual language loading logic
        console.log(`Loading language: ${locale}`)
    }
}

// Export the optimizer and queue
export const i18nOptimizer = I18nOptimizer.getInstance()
export const languageQueue = new LanguageQueue()

// Utility function to measure i18n performance
export const measureI18nPerformance = (operation: string, fn: () => any): any => {
    const start = performance.now()
    const result = fn()
    const end = performance.now()
    
    // debug: console.log(`i18n ${operation} took ${(end - start).toFixed(2)}ms`)
    return result
}

// Debounced language switching for better performance
export const debouncedLanguageSwitch = (() => {
    let timeoutId: NodeJS.Timeout | null = null
    
    return (locale: string, switchFunction: (locale: string) => void, delay: number = 300) => {
        if (timeoutId) {
            clearTimeout(timeoutId)
        }
        
        timeoutId = setTimeout(() => {
            switchFunction(locale)
            timeoutId = null
        }, delay)
    }
})()

// Export types for use in other files
export type { LanguageCache }
